import { Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { SEARCH_INDEX, SearchEntry } from './search-content.generated';

const MAX_RESULTS = 8;
const SNIPPET_RADIUS = 40;

export interface SearchResult {
  entry: SearchEntry;
  snippet: string | null;
}

@Component({
  selector: 'app-search',
  templateUrl: './search.component.html',
  styleUrls: ['./search.component.css']
})
export class SearchComponent implements OnInit, OnDestroy {

  isOpen = false;
  query = '';
  results: SearchResult[] = [];
  highlightedIndex = -1;
  panelTop = 0;

  @ViewChild('searchInput') searchInput: ElementRef<HTMLInputElement>;

  private querySubject = new Subject<string>();
  private querySubscription: Subscription;

  constructor(private elementRef: ElementRef, private router: Router) { }

  ngOnInit() {
    this.querySubscription = this.querySubject.pipe(
      debounceTime(150),
      distinctUntilChanged()
    ).subscribe((query) => this.filterResults(query));
  }

  ngOnDestroy() {
    if (this.querySubscription) {
      this.querySubscription.unsubscribe();
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    if (this.isOpen && !this.elementRef.nativeElement.contains(event.target)) {
      this.close();
    }
  }

  toggleOpen() {
    this.isOpen = !this.isOpen;
    if (this.isOpen) {
      const header = document.getElementById('header');
      this.panelTop = header ? header.getBoundingClientRect().bottom : 0;
      setTimeout(() => {
        if (this.searchInput) {
          this.searchInput.nativeElement.focus();
        }
      });
    } else {
      this.reset();
    }
  }

  onQueryChange(query: string) {
    this.querySubject.next(query);
  }

  onKeydown(event: KeyboardEvent) {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (this.results.length) {
        this.highlightedIndex = (this.highlightedIndex + 1) % this.results.length;
      }
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (this.results.length) {
        this.highlightedIndex = (this.highlightedIndex - 1 + this.results.length) % this.results.length;
      }
    } else if (event.key === 'Enter') {
      event.preventDefault();
      if (this.highlightedIndex >= 0 && this.highlightedIndex < this.results.length) {
        this.selectResult(this.results[this.highlightedIndex]);
      }
    } else if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
    }
  }

  selectResult(result: SearchResult) {
    this.router.navigate([result.entry.route]);
    this.close();
  }

  private filterResults(query: string) {
    const trimmed = query.trim().toLowerCase();
    this.highlightedIndex = -1;
    if (!trimmed) {
      this.results = [];
      return;
    }
    const titleMatches: SearchResult[] = [];
    const contentMatches: SearchResult[] = [];
    for (const entry of SEARCH_INDEX) {
      if (entry.title.toLowerCase().includes(trimmed)) {
        titleMatches.push({ entry, snippet: null });
        continue;
      }
      const lowerContent = entry.content.toLowerCase();
      const matchIndex = lowerContent.indexOf(trimmed);
      if (matchIndex !== -1) {
        contentMatches.push({ entry, snippet: this.buildSnippet(entry.content, matchIndex, trimmed.length) });
      }
    }
    this.results = [...titleMatches, ...contentMatches].slice(0, MAX_RESULTS);
  }

  private buildSnippet(content: string, matchIndex: number, matchLength: number): string {
    const start = Math.max(0, matchIndex - SNIPPET_RADIUS);
    const end = Math.min(content.length, matchIndex + matchLength + SNIPPET_RADIUS);
    let snippet = content.slice(start, end).trim();
    if (start > 0) {
      snippet = '...' + snippet;
    }
    if (end < content.length) {
      snippet = snippet + '...';
    }
    return snippet;
  }

  private close() {
    this.isOpen = false;
    this.reset();
  }

  private reset() {
    this.query = '';
    this.results = [];
    this.highlightedIndex = -1;
  }
}
